import React, { useMemo, useState } from "react";

import useTheme from "hooks/observer";

import { PRIVILEGED_OP_TYPES } from "./_shared/constants";
import { useYsentinel } from "../data/YsentinelDataContext";
import { rangeWindow } from "../data/defaults";

import {
  KpiCard,
  ChartCard,
  Badge,
  Loader,
  EmptyHint,
  ErrorHint,
} from "./_shared/primitives";
import EChart, { echartsPalette, TABLEAU_10 } from "./_shared/EChart";
import { StorageDistribution, AccessByLocation, OperationCountOverTime, TopMovers, ExternalShareTopUsers, OffHoursRatio, ManageUser as MU, GetRemovedUsersFromLogs, CollaborationFlow } from "../data/queuedSdk";
import { StoragePlan } from "@induz/sdk";
import "./_shared/dashboard.scss";



const SummaryDashboard = () => {
  const mode = useTheme();
  const palette = echartsPalette(mode);
  const { range, overview, users: usersState } = useYsentinel();
  const [realStorage, setRealStorage] = useState({ usedGb: 0, totalGb: 100, seatsPurchased: 0 });

  React.useEffect(() => {
    const fetchData = async () => {
      try {
        // --- Fetch Storage & Plan Info ---
        const userInfo = await MU.getUserDetails(undefined, false);
        const planId = userInfo?.getPlan?.();
        let limitInGb = 100;
        let seatsPurchased = 0;

        if (typeof planId === "number") {
          const storagePlanObj = new StoragePlan();
          storagePlanObj.setPlanId(planId);
          const plan = await storagePlanObj.getPlan();
          const storageBytes = plan.getStorage();
          if (storageBytes != null) {
            const b = typeof storageBytes === "number" ? storageBytes : (storageBytes?.toNumber?.() ?? 0);
            limitInGb = b / (1024 ** 3);
          }
          const minUsers = plan.getMinUsers ? plan.getMinUsers() : 0;
          seatsPurchased = typeof minUsers === "number" ? minUsers : (minUsers?.toNumber?.() ?? 0);
        } else if (sessionStorage.getItem("memory")) {
          limitInGb = parseInt(sessionStorage.getItem("memory"), 10);
        }

        const sizeRaw = await MU.getStorageSize();
        const size = typeof sizeRaw === "number" ? sizeRaw : (sizeRaw?.toNumber?.() ?? 0);
        const usedGb = Math.round((size / (1024 ** 3)) * 100) / 100;

        // --- Fetch Users (Active + Removed) ---
        // const now = Date.now();
        // const thirtyDaysAgo = now - 30 * 86_400_000;

        const [userList, /* removedUserEmails */] = await Promise.all([
          MU.listUsers(),
          // GetRemovedUsersFromLogs(thirtyDaysAgo, now).catch(() => []),
        ]);
        const removedUserEmails = []; // Temporarily disable removed users.

        const actualUsers = userList.map((user) => ({
          email: user.email,
          active: !user.isBlocked,
          status: user.isBlocked ? "blocked" : "active",
        }));

        const activeEmailSet = new Set(actualUsers.map(u => u.email));
        const removedUserEntries = (removedUserEmails || [])
          .filter(email => !activeEmailSet.has(email))
          .map(email => ({
            email,
            active: false,
            status: "removed",
          }));

        const combinedUsers = [...actualUsers, ...removedUserEntries];

        if (usersState?.setUsersList) {
          usersState.setUsersList(combinedUsers);
        }

        setRealStorage({ usedGb, totalGb: limitInGb, seatsPurchased });
      } catch (err) {
        console.error("Error fetching dashboard data:", err);
      }
    };
    fetchData();
  }, []);

  const {
    setStorage,
    setActivity,
    setDeniedDaily,
    setTopOperations,
    setPrivilegedDaily,
    setKpis,
    setTopMovers,
    setLocations,
    setExternalShareRankByUser,
    setExternalShareFlow,
    org,
    kpis,
    activity,
    storage,
    privilegedDaily,
    externalShareFlow,
    topMovers,
    externalShareRankByUser,
    externalShareRankByTeam,
    locations,
    offHoursRatio,
    setOffHoursRatio,
    deniedDaily,
    topOperations,
  } = overview;

  const [loadingStorage, setLoadingStorage] = useState(true);
  const [loadingOps, setLoadingOps] = useState(true);
  const [loadingMovers, setLoadingMovers] = useState(true);
  const [loadingLocations, setLoadingLocations] = useState(true);
  const [loadingExternalShare, setLoadingExternalShare] = useState(true);
  const [loadingOffHours, setLoadingOffHours] = useState(true);

  const [errorOps, setErrorOps] = useState(null);
  const [errorMovers, setErrorMovers] = useState(null);
  const [errorLocations, setErrorLocations] = useState(null);
  const [errorExternalShare, setErrorExternalShare] = useState(null);
  const [errorOffHours, setErrorOffHours] = useState(null);
  const [errorStorage, setErrorStorage] = useState(null);

  const [retryTrigger, setRetryTrigger] = useState(0);
  const retry = () => setRetryTrigger(prev => prev + 1);


  const kpisRef = React.useRef(kpis);
  React.useEffect(() => {
    kpisRef.current = kpis;
  }, [kpis]);

  React.useEffect(() => {
    const { start: startDate, end: now } = rangeWindow(range);

    setLoadingOps(true);
    setLoadingMovers(true);
    setLoadingLocations(true);
    setLoadingExternalShare(true);
    setErrorOps(null);
    setErrorMovers(null);
    setErrorLocations(null);
    setErrorExternalShare(null);

    OperationCountOverTime(startDate, now).then((res) => {
      setTopOperations(res.CountOfOperations.sort((a, b) => b.count - a.count).slice(0, 10));

      const act = res.operationsOverTime.date.map((d, i) => ({
        date: d.split("T")[0],
        reads: res.operationsOverTime.reads[i],
        writes: res.operationsOverTime.writes[i],
        shares: res.operationsOverTime.shares[i],
        deletes: res.operationsOverTime.deletes[i],
      }));
      setActivity(act);

      const denied = res.operationsOverTime.date.map((d, i) => ({
        day: d.split("T")[0],
        denied: res.operationsOverTime.denied[i],
      }));
      setDeniedDaily(denied);
      setPrivilegedDaily(res.privilegedDaily);

      const privTotal = res.privilegedDaily.reduce((acc, row) => {
        return acc + Object.keys(row).filter((k) => k !== "date").reduce((sum, k) => sum + (row[k] || 0), 0);
      }, 0);
      const denTotal = res.operationsOverTime.denied.reduce((a, b) => a + b, 0);
      setKpis({ ...kpisRef.current, privilegedTotal: privTotal, deniedTotal: denTotal });
    }).catch(err => {
      console.error(err);
      setErrorOps("Failed to load operations data");
    }).finally(() => setLoadingOps(false));

    TopMovers(startDate, now).then((res) => {
      setTopMovers({
        downloads: res.downloads.slice(0, 10),
        uploads: res.uploads.slice(0, 10),
        shares: res.shares.slice(0, 10),
        activity: res.activity.slice(0, 10),
      });
    }).catch(err => {
      console.error(err);
      setErrorMovers("Failed to load top movers");
    }).finally(() => setLoadingMovers(false));

    AccessByLocation(startDate, now).then((res) => {
      setLocations(res.slice(0, 10));
    }).catch(err => {
      console.error(err);
      setErrorLocations("Failed to load location data");
    }).finally(() => setLoadingLocations(false));

    ExternalShareTopUsers(startDate, now).then((res) => {
      setExternalShareRankByUser(res.slice(0, 10));
    }).catch(err => {
      console.error(err);
      setErrorExternalShare("Failed to load external sharing data");
    }).finally(() => setLoadingExternalShare(false));

    CollaborationFlow(startDate, now).then((res) => {
      setExternalShareFlow(res);
    }).catch(console.error);

    setLoadingOffHours(true);
    setErrorOffHours(null);
    OffHoursRatio(startDate, now).then((res) => {
      setOffHoursRatio(res);
    }).catch(err => {
      console.error(err);
      setErrorOffHours("Failed to load off-hours analysis");
    }).finally(() => setLoadingOffHours(false));

    // --- Storage distribution (snapshot) ---
    setLoadingStorage(true);
    setErrorStorage(null);
    StorageDistribution(startDate, now).then(buckets => {
      setStorage({
        byType: buckets || [],
        byTeam: buckets || [],
        byRegion: buckets || []
      });
    }).catch(err => {
      console.error("Failed to fetch StorageDistribution from SDK:", err);
      setErrorStorage("Failed to load storage distribution");
    }).finally(() => {
      setLoadingStorage(false);
    });


  }, [range, retryTrigger, setTopOperations, setActivity, setDeniedDaily, setPrivilegedDaily, setKpis, setTopMovers, setLocations, setExternalShareRankByUser, setOffHoursRatio, setStorage, setExternalShareFlow]);





  const [storageMetric, setStorageMetric] = useState("size");          // "size" | "count"
  const [storageDimension, setStorageDimension] = useState("type");    // "type" | "team" | "region"
  const [topMoversTab, setTopMoversTab] = useState("downloads");       // "downloads" | "uploads" | "shares" | "activity"
  const [locationView, setLocationView] = useState("bars");            // "bars" | "table"

  // --- Ops over time --------------------------------------------------------
  const opsTimelineOption = useMemo(() => {
    const days = activity.map((p) => p.date);
    const reads = activity.map((p) => p.reads);
    const writes = activity.map((p) => p.writes);
    const shares = activity.map((p) => p.shares);
    const deletes = activity.map((p) => p.deletes);
    return {
      backgroundColor: "transparent",
      color: [palette.opClass.read, palette.opClass.write, palette.opClass.share, palette.opClass.destructive],
      grid: { top: 30, right: 16, bottom: 52, left: 44 },
      tooltip: { trigger: "axis", backgroundColor: palette.tooltipBg, borderColor: palette.tooltipBorder, textStyle: { color: palette.text } },
      legend: { top: 0, textStyle: { color: palette.textSoft, fontSize: 11 }, icon: "roundRect" },
      xAxis: {
        type: "category",
        data: days,
        axisLine: { lineStyle: { color: palette.grid } },
        axisLabel: { color: palette.textSoft, fontSize: 11 },
      },
      yAxis: {
        type: "value",
        splitLine: { lineStyle: { color: palette.grid } },
        axisLabel: { color: palette.textSoft, fontSize: 11 },
      },
      dataZoom: [
        { type: "inside", start: 0, end: 100 },
        { type: "slider", height: 18, bottom: 6, borderColor: palette.grid, textStyle: { color: palette.textSoft, fontSize: 10 } },
      ],
      series: [
        { name: "Reads", type: "line", stack: "ops", areaStyle: { opacity: 0.35 }, smooth: true, data: reads, symbol: "none" },
        { name: "Writes", type: "line", stack: "ops", areaStyle: { opacity: 0.35 }, smooth: true, data: writes, symbol: "none" },
        { name: "Shares", type: "line", stack: "ops", areaStyle: { opacity: 0.35 }, smooth: true, data: shares, symbol: "none" },
        { name: "Deletes", type: "line", stack: "ops", areaStyle: { opacity: 0.35 }, smooth: true, data: deletes, symbol: "none" },
      ],
    };
  }, [palette, activity]);

  // --- Storage treemap — dimension (type | team | region) × measure (size | count)
  const { storageOption, othersText } = useMemo(() => {
    // If storage is mock or empty, returning early will be handled in JSX. 
    // Here we just prepare the chart option if data is available.
    const source =
      storageDimension === "team" ? storage?.byTeam :
        storageDimension === "region" ? storage?.byRegion :
          storage?.byType;

    if (!source || source.length === 0) {
      return { storageOption: { backgroundColor: "transparent", series: [] }, othersText: null };
    }

    const measureUnit = storageMetric === "size" ? "GB" : "files";
    const data = source.map((f) => ({
      name: f.key,
      value: storageMetric === "size" ? f.gb : f.count,
    }));

    let othersTextStr = null;
    // Extract "Others" so it doesn't dominate the map, show as text below
    const othersNodeIndex = data.findIndex(d => d.name.toLowerCase() === 'others' || d.name.toLowerCase() === 'other');
    if (othersNodeIndex !== -1) {
      const original = source.find(s => s.key === data[othersNodeIndex].name);
      const val = original ? (storageMetric === "size" ? original.gb : original.count) : data[othersNodeIndex].value;
      othersTextStr = `Others: ${val.toLocaleString()} ${measureUnit}`;
      data.splice(othersNodeIndex, 1); // remove from treemap
    }

    const option = {
      backgroundColor: "transparent",
      color: TABLEAU_10,
      tooltip: {
        backgroundColor: palette.tooltipBg,
        borderColor: palette.tooltipBorder,
        textStyle: { color: palette.text },
        formatter: (p) => {
          const original = source.find(s => s.key === p.name);
          const val = original ? (storageMetric === "size" ? original.gb : original.count) : p.value;
          return `${p.marker} <b>${p.name}</b><br/>${val.toLocaleString()} ${measureUnit}`;
        },
      },
      series: [
        {
          type: "treemap",
          roam: false,
          nodeClick: false,
          breadcrumb: { show: false },
          data,
          label: {
            position: "insideTopLeft",
            color: "#fff",
            fontSize: 12,
            fontWeight: 700,
            formatter: (p) => {
              const original = source.find(s => s.key === p.name);
              const val = original ? (storageMetric === "size" ? original.gb : original.count) : p.value;
              return `${p.name}\n${val.toLocaleString()} ${measureUnit}`;
            },
          },
          upperLabel: { show: false },
          levels: [{ itemStyle: { borderColor: palette.cardBg, borderWidth: 2, gapWidth: 2 } }],
        },
      ],
    };

    return { storageOption: option, othersText: othersTextStr };
  }, [palette, storageMetric, storageDimension, storage]);


  // --- Top operations -------------------------------------------------------
  const topOpsOption = useMemo(() => {
    const ordered = [...topOperations].sort((a, b) => a.count - b.count);
    return {
      backgroundColor: "transparent",
      grid: { top: 10, right: 40, bottom: 36, left: 140 },
      tooltip: { trigger: "axis", backgroundColor: palette.tooltipBg, borderColor: palette.tooltipBorder, textStyle: { color: palette.text } },
      xAxis: {
        type: "value",
        axisLine: { lineStyle: { color: palette.grid } },
        splitLine: { lineStyle: { color: palette.grid } },
        axisLabel: { color: palette.textSoft, fontSize: 11 },
      },
      yAxis: {
        type: "category",
        data: ordered.map((o) => o.operation),
        axisLine: { lineStyle: { color: palette.grid } },
        axisLabel: { color: palette.textSoft, fontSize: 11, width: 100, overflow: "truncate", ellipsis: "..." },
      },
      series: [
        {
          type: "bar",
          data: ordered.map((o) => o.count),
          itemStyle: { borderRadius: [0, 6, 6, 0], color: palette.brand },
          label: { show: true, position: "right", color: palette.text, fontSize: 11 },
        },
      ],
    };
  }, [palette, topOperations]);

  // --- Admin activity calendar (28 days × ops/day) --------------------------
  // Single-actor reality: an actor×op matrix degenerates. The actionable
  // questions are *when* admin activity happens (weekend/off-cadence spikes
  // flag anomalies) and *how much*. ECharts' calendar coordinate system aligns
  // cells to real Mon–Sun columns automatically.
  const adminCalendarOption = useMemo(() => {
    const rows = privilegedDaily;
    const isDark = mode === "dark";
    if (!rows.length) {
      return { backgroundColor: "transparent", series: [] };
    }
    const data = rows.map((r) => [
      r.date,
      PRIVILEGED_OP_TYPES.reduce((a, op) => a + (r[op] || 0), 0),
    ]);
    const maxDaily = Math.max(...data.map(([, v]) => v), 1);
    const start = rows[0].date;
    const end = rows[rows.length - 1].date;
    return {
      backgroundColor: "transparent",
      tooltip: {
        backgroundColor: palette.tooltipBg,
        borderColor: palette.tooltipBorder,
        textStyle: { color: palette.text },
        formatter: (p) => {
          const [date, count] = p.data;
          return `<b>${date}</b><br/>${count} privileged op${count === 1 ? "" : "s"}`;
        },
      },
      visualMap: {
        min: 0,
        max: maxDaily,
        calculable: false,
        orient: "horizontal",
        left: "center",
        bottom: 4,
        itemWidth: 10,
        itemHeight: 80,
        textStyle: { color: palette.textSoft, fontSize: 10 },
        inRange: {
          // Dark mode: ramp from near-bg → vivid purple so zero cells blend
          // into the card background instead of showing as light lavender.
          color: isDark
            ? ["#1f1529", "#3a1f54", "#6a2fa0", "#8237bb", "#5a1f8a"]
            : ["#f3eafd", "#dcc4f4", "#b079ee", "#8237bb", "#5a1f8a"],
        },
      },
      calendar: {
        top: 16,
        bottom: 56,
        left: 40,
        right: 16,
        range: [start, end],
        cellSize: ["auto", "auto"],
        splitLine: { lineStyle: { color: palette.grid } },
        itemStyle: {
          color: palette.cardBg,
          borderColor: palette.grid,
          borderWidth: 1,
        },
        dayLabel: {
          firstDay: 1, // Monday
          color: palette.textSoft,
          fontSize: 10,
          nameMap: ["S", "M", "T", "W", "T", "F", "S"],
        },
        monthLabel: {
          color: palette.textSoft,
          fontSize: 10,
        },
        yearLabel: { show: false },
      },
      series: [
        {
          type: "heatmap",
          coordinateSystem: "calendar",
          data,
          label: {
            show: true,
            color: palette.text,
            fontSize: 10,
            fontWeight: 600,
            formatter: (p) => (p.data[1] > 0 ? p.data[1] : ""),
          },
        },
      ],
    };
  }, [palette, mode, privilegedDaily]);


  // --- Op-type donut (share of admin actions in window) ---------------------
  const adminDonutOption = useMemo(() => {
    const totals = PRIVILEGED_OP_TYPES.map((op) => ({
      name: op,
      value: privilegedDaily.reduce((a, r) => a + (r[op] || 0), 0),
    }));
    const grand = totals.reduce((a, t) => a + t.value, 0);
    return {
      backgroundColor: "transparent",
      color: TABLEAU_10,
      tooltip: {
        backgroundColor: palette.tooltipBg,
        borderColor: palette.tooltipBorder,
        textStyle: { color: palette.text },
        formatter: (p) =>
          `<b>${p.name}</b><br/>${p.value} ops · ${grand ? ((p.value / grand) * 100).toFixed(1) : 0}%`,
      },
      legend: {
        type: "scroll",
        orient: "vertical",
        right: 4,
        top: "middle",
        textStyle: { color: palette.text, fontSize: 11 },
        itemWidth: 10,
        itemHeight: 10,
      },
      series: [
        {
          type: "pie",
          radius: ["52%", "78%"],
          center: ["32%", "50%"],
          avoidLabelOverlap: true,
          itemStyle: { borderColor: palette.cardBg, borderWidth: 2, borderRadius: 4 },
          label: { show: false },
          labelLine: { show: false },
          data: totals,
        },
      ],
    };
  }, [palette, privilegedDaily]);

  // --- Access by location (bar with risk-tier color) ------------------------
  const locationOption = useMemo(() => {
    const sorted = [...locations].sort((a, b) => b.events - a.events);
    const TIER_COLOR = {
      trusted: palette.success || "#10b981",
      allowed: palette.warning || "#f59e0b",
      blocked: palette.danger || "#ef4444",
    };
    return {
      backgroundColor: "transparent",
      grid: { top: 8, right: 56, bottom: 36, left: 168 },
      tooltip: {
        trigger: "axis",
        backgroundColor: palette.tooltipBg,
        borderColor: palette.tooltipBorder,
        textStyle: { color: palette.text },
        formatter: (params) => {
          const p = Array.isArray(params) ? params[0] : params;
          const row = sorted[sorted.length - 1 - p.dataIndex];
          if (!row) return '';
          return (
            `<b>${row.label}</b><br/>` +
            `${row.events.toLocaleString()} events · ${row.unique_users} users<br/>` +
            `Status: <b style="color:${TIER_COLOR[row.tier]}">${row.tier}${row.allowed ? "" : " · blocked"}</b>`
          );
        },
      },
      xAxis: {
        type: "value",
        axisLine: { lineStyle: { color: palette.grid } },
        splitLine: { lineStyle: { color: palette.grid } },
        axisLabel: { color: palette.textSoft, fontSize: 11 },
      },
      yAxis: {
        type: "category",
        // Reverse so the heaviest row sits at the top of the chart.
        data: sorted.map((r) => r.label).reverse(),
        axisLine: { lineStyle: { color: palette.grid } },
        axisTick: { show: false },
        axisLabel: { color: palette.text, fontSize: 11, width: 150, overflow: "truncate", ellipsis: "..." },
      },
      series: [
        {
          type: "bar",
          data: [...sorted].reverse().map((r) => ({
            value: r.events,
            itemStyle: { color: TIER_COLOR[r.tier], borderRadius: [0, 6, 6, 0] },
          })),
          label: {
            show: true,
            position: "right",
            color: palette.text,
            fontSize: 11,
            formatter: (p) => p.value.toLocaleString(),
          },
        },
      ],
    };
  }, [palette, locations]);

  // --- External share Sankey ------------------------------------------------
  const sankeyOption = useMemo(() => {
    if (!externalShareFlow || externalShareFlow.length === 0) {
      return { backgroundColor: "transparent", series: [] };
    }

    // Grouping threshold: nodes contributing < 2% of total flow in their level are grouped as "Other"
    const totalFlow = externalShareFlow.reduce((sum, item) => sum + item.count, 0);
    const threshold = totalFlow * 0.02;

    const spaceCounts = new Map();
    externalShareFlow.forEach(i => spaceCounts.set(i.space, (spaceCounts.get(i.space) || 0) + i.count));

    const processedFlow = externalShareFlow.map(item => ({
      ...item,
      space: spaceCounts.get(item.space) < threshold ? "Other Spaces" : item.space
    }));

    const nodesMap = new Map();
    const linksMap = new Map();

    const addNode = (name, level) => {
      if (!nodesMap.has(name)) {
        let color = palette.textSoft;
        if (level === 0) color = TABLEAU_10[nodesMap.size % TABLEAU_10.length];
        if (level === 1) color = palette.grid;
        if (level === 2) color = palette.brand || "#5b73e8";

        nodesMap.set(name, {
          name,
          depth: level,
          itemStyle: {
            color,
            borderColor: "rgba(0,0,0,0.1)",
            borderWidth: 1,
            opacity: 0.9
          }
        });
      }
    };

    const addLink = (source, target, value) => {
      const key = `${source} -> ${target}`;
      linksMap.set(key, (linksMap.get(key) || 0) + value);
    };

    processedFlow.forEach((item) => {
      const { actor, space, outcome, count } = item;
      addNode(actor, 0);
      addNode(space, 1);
      addNode(outcome, 2);
      addLink(actor, space, count);
      addLink(space, outcome, count);
    });

    const nodes = Array.from(nodesMap.values());
    const links = Array.from(linksMap.entries()).map(([key, value]) => {
      const [source, target] = key.split(" -> ");
      return { source, target, value };
    });

    return {
      backgroundColor: "transparent",
      tooltip: {
        trigger: "item",
        triggerOn: "mousemove",
        backgroundColor: palette.tooltipBg,
        borderColor: palette.tooltipBorder,
        textStyle: { color: palette.text, fontSize: 12 },
        formatter: (p) => {
          if (p.dataType === "edge") {
            return `<div style="padding:4px">
              <span style="color:${palette.textSoft}">${p.data.source}</span> 
              <b style="margin:0 4px">→</b> 
              <span style="color:${palette.textSoft}">${p.data.target}</span><br/>
              <b style="font-size:14px">${p.data.value}</b> <small>actions</small>
            </div>`;
          }
          return `<div style="padding:4px"><b>${p.name}</b><br/>${p.value} total actions</div>`;
        }
      },
      series: [
        {
          type: "sankey",
          data: nodes,
          links,
          emphasis: { focus: "adjacency" },
          nodeWidth: 18,
          nodeGap: 18,
          nodeAlign: "justify",
          layoutIterations: 64,
          draggable: false,
          lineStyle: {
            color: "gradient",
            curveness: 0.6,
            opacity: 0.35,
            borderColor: "rgba(255,255,255,0.1)",
            borderWidth: 0.5
          },
          label: {
            color: palette.text,
            fontSize: 10,
            fontWeight: 400,
            formatter: (p) => {
              if (p.name.length > 20) return p.name.substring(0, 17) + "...";
              return p.name;
            }
          },
        },
      ],
    };
  }, [palette, externalShareFlow]);

  // --- Denied ops trend -----------------------------------------------------
  const deniedOption = useMemo(() => {
    return {
      backgroundColor: "transparent",
      grid: { top: 24, right: 12, bottom: 36, left: 36 },
      tooltip: { trigger: "axis", backgroundColor: palette.tooltipBg, borderColor: palette.tooltipBorder, textStyle: { color: palette.text } },
      xAxis: {
        type: "category",
        data: deniedDaily.map((r) => r.day),
        axisLine: { lineStyle: { color: palette.grid } },
        axisLabel: { color: palette.textSoft, fontSize: 11 },
      },
      yAxis: {
        type: "value",
        splitLine: { lineStyle: { color: palette.grid } },
        axisLabel: { color: palette.textSoft, fontSize: 11 },
      },
      series: [
        {
          type: "line",
          smooth: true,
          data: deniedDaily.map((r) => r.denied),
          areaStyle: { opacity: 0.25, color: palette.danger },
          lineStyle: { color: palette.danger, width: 2 },
          itemStyle: { color: palette.danger },
          symbol: "circle",
          symbolSize: 6,
        },
      ],
    };
  }, [palette, deniedDaily]);

  // --- Off-hours gauge ------------------------------------------------------
  const offHoursOption = useMemo(() => {
    return {
      backgroundColor: "transparent",
      series: [
        {
          type: "gauge",
          startAngle: 210,
          endAngle: -30,
          min: 0,
          max: 100,
          progress: { show: true, width: 16, itemStyle: { color: palette.warning } },
          axisLine: { lineStyle: { width: 16, color: [[1, palette.grid]] } },
          axisTick: { show: false },
          splitLine: { show: false },
          axisLabel: { show: false },
          pointer: { show: false },
          anchor: { show: false },
          title: { show: false },
          detail: {
            valueAnimation: true,
            fontSize: 26,
            color: palette.text,
            fontWeight: 700,
            formatter: "{value}%",
            offsetCenter: [0, "5%"],
          },
          data: [{ value: offHoursRatio.offHours }],
        },
      ],
    };
  }, [palette]);

  // --- Top movers (downloads | uploads | shares | activity) ----------------
  // One card with four lenses on the same "who's moving the most" question.
  // Each tab swaps its data source and value formatter; the chart structure
  // (horizontal bar, top-quartile flagged) stays identical so the user's eye
  // doesn't have to recalibrate when switching tabs.
  const topMoversOption = useMemo(() => {
    const windowLabel = `in ${range}`;
    const tab = (
      topMoversTab === "uploads" ? {
        rows: topMovers.uploads,
        format: (v) => v.toLocaleString(),
        flagColor: palette.warning,    // upload spike = capacity / cost signal
        tooltipSuffix: `write ops ${windowLabel}`,
      } :
        topMoversTab === "shares" ? {
          rows: topMovers.shares,
          format: (v) => v.toLocaleString(),
          flagColor: palette.warning,
          tooltipSuffix: `share ops ${windowLabel}`,
        } :
          topMoversTab === "activity" ? {
            rows: topMovers.activity,
            format: (v) => v.toLocaleString(),
            flagColor: palette.warning,
            tooltipSuffix: `operations ${windowLabel}`,
          } : {
            rows: topMovers.downloads,
            format: (v) => v.toLocaleString(),
            flagColor: palette.danger,     // download spike = exfil signal
            tooltipSuffix: `read ops ${windowLabel}`,
          }
    );
    const ordered = [...tab.rows].sort((a, b) => a.value - b.value);
    const max = Math.max(...ordered.map((o) => o.value), 1);
    return {
      backgroundColor: "transparent",
      grid: { top: 8, right: 64, bottom: 36, left: 180 },
      tooltip: {
        trigger: "axis",
        backgroundColor: palette.tooltipBg,
        borderColor: palette.tooltipBorder,
        textStyle: { color: palette.text },
        formatter: (params) => {
          const p = Array.isArray(params) ? params[0] : params;
          return `<b>${p.name}</b><br/>${tab.format(p.value)} ${tab.tooltipSuffix}`;
        },
      },
      xAxis: {
        type: "value",
        axisLine: { lineStyle: { color: palette.grid } },
        splitLine: { lineStyle: { color: palette.grid } },
        axisLabel: {
          color: palette.textSoft,
          fontSize: 11,
          formatter: (v) => tab.format(v),
        },
      },
      yAxis: {
        type: "category",
        data: ordered.map((o) => o.label || o.key),
        axisLine: { lineStyle: { color: palette.grid } },
        axisTick: { show: false },
        axisLabel: { color: palette.textSoft, fontSize: 11, width: 125, overflow: "truncate", ellipsis: "..." },
      },
      series: [
        {
          type: "bar",
          data: ordered.map((o) => ({
            value: o.value,
            itemStyle: {
              color: o.value / max > 0.8 ? tab.flagColor : palette.brand,
              borderRadius: [0, 6, 6, 0],
            },
          })),
          label: {
            show: true,
            position: "right",
            color: palette.text,
            fontSize: 11,
            formatter: (p) => tab.format(p.value),
          },
        },
      ],
    };
  }, [palette, topMoversTab, topMovers, range]);

  // --- External shares ranked by user/team ---------------------------------
  // The provider already aggregates EXTERNAL_SHARE_FLOW into ranked lists (see
  // defaults.js → buildExternalShareRanks); this card just chooses which list.
  const externalShareRankOption = useMemo(() => {
    const source = externalShareRankByUser;
    // Ascending so the largest row sits at the top of the chart after y-axis flip.
    const ordered = [...source].sort((a, b) => a.value - b.value);

    return {
      backgroundColor: "transparent",
      legend: {
        show: true,
        bottom: 0,
        textStyle: { color: palette.textSoft, fontSize: 10 },
        itemWidth: 12,
        itemHeight: 12,
      },
      grid: { top: 20, right: 20, bottom: 60, left: 140 },
      tooltip: {
        trigger: "axis",
        axisPointer: { type: "shadow" },
        backgroundColor: palette.tooltipBg,
        borderColor: palette.tooltipBorder,
        textStyle: { color: palette.text },
      },
      xAxis: {
        type: "value",
        axisLine: { lineStyle: { color: palette.grid } },
        splitLine: { lineStyle: { color: palette.grid } },
        axisLabel: { color: palette.textSoft, fontSize: 11 },
      },
      yAxis: {
        type: "category",
        data: ordered.map((r) => r.label || r.key),
        axisLine: { lineStyle: { color: palette.grid } },
        axisTick: { show: false },
        axisLabel: { color: palette.text, fontSize: 11, width: 125, overflow: "truncate", ellipsis: "..." },
      },
      series: [
        {
          name: "Adds",
          type: "bar",
          stack: "total",
          data: ordered.map((r) => r.adds || 0),
          itemStyle: { color: palette.brand, borderRadius: [0, 0, 0, 0] },
        },
        {
          name: "Updates",
          type: "bar",
          stack: "total",
          data: ordered.map((r) => r.updates || 0),
          itemStyle: { color: palette.warning, borderRadius: [0, 0, 0, 0] },
        },
        {
          name: "Removals",
          type: "bar",
          stack: "total",
          data: ordered.map((r) => r.removals || 0),
          itemStyle: { color: palette.error, borderRadius: [0, 4, 4, 0] },
        },
      ],
    };
  }, [palette, externalShareRankByUser]);

  // --- Counters for KPI row -------------------------------------------------
  // Kpis come pre-aggregated from the provider so we don't recompute per render.
  const {
    externalShares: externalShareCount,
    privilegedTotal,
    deniedTotal,
  } = kpis;

  const allUsers = usersState?.users || [];
  const activeUsers = allUsers.filter(u => u.status === "active").length;
  const blockedUsers = allUsers.filter(u => u.status === "blocked").length;
  const removedUsersCount = allUsers.filter(u => u.status === "removed").length;
  const seatsUsedCount = activeUsers + blockedUsers;

  const pctUsed = realStorage.totalGb > 0 ? (realStorage.usedGb / realStorage.totalGb) * 100 : 0;

  return (
    <div className="ysd-page">
      {/* High-signal KPI strip — capacity, license, and the three security signals
          auditors look at first. Volume / spaces metrics live inside their own charts. */}
      <div className="ysd-grid ysd-grid--kpis">
        <KpiCard
          label="Storage used"
          value={`${realStorage.usedGb.toLocaleString()} GB`}
          delta={`${pctUsed.toFixed(1)}%`}
          trend="up"
          hint={`of ${realStorage.totalGb.toLocaleString()} GB`}
          accent="info"
        />
        <KpiCard
          label="Active users"
          value={activeUsers}
          hint={`${seatsUsedCount} / ${realStorage.seatsPurchased > 0 ? realStorage.seatsPurchased : org.seats_purchased} seats · ${removedUsersCount} removed`}
          accent="success"
        />
        {false && <KpiCard
          label="External shares"
          value={externalShareCount}
          hint={`${org.external_users} external users · last ${range}`}
          accent="warning"
        />}
        <KpiCard
          label={`Privileged ops · ${range}`}
          value={privilegedTotal.toLocaleString()}
          hint="role / team / share mgmt"
          accent="warning"
        />
        <KpiCard
          label={`Denied ops · ${range}`}
          value={deniedTotal.toLocaleString()}
          hint="permission-blocked attempts"
          accent="danger"
        />
      </div>

      <div className="ysd-grid ysd-grid--2">
        <ChartCard
          title={`Operations · ${range}`}
          subtitle="Cumulative reads, writes, shares, and deletes"
          span={8}
        >
          {loadingOps ? (
            <Loader message="Loading activity..." minHeight={320} />
          ) : errorOps ? (
            <ErrorHint message={errorOps} onRetry={retry} />
          ) : !activity.length ? (
            <EmptyHint>No activity recorded in the active range.</EmptyHint>
          ) : (
            <EChart option={opsTimelineOption} height={320} />
          )}
        </ChartCard>

        <ChartCard
          title="Data lifecycle storage"
          subtitle="Distribution by size or object count · excludes deleted versions"
          span={4}
          right={
            <>
              <div className="ysd-toggle">
                <button className={storageMetric === "size" ? "active" : ""} onClick={() => setStorageMetric("size")}>Size</button>
                <button className={storageMetric === "count" ? "active" : ""} onClick={() => setStorageMetric("count")}>Count</button>
              </div>
              {/* Hide Team and Region options as requested */}
            </>
          }
        >
          {loadingStorage ? (
            <Loader message="Fetching storage distribution..." minHeight={360} />
          ) : (
            <div style={{ display: "flex", flexDirection: "column", height: "100%" }}>
              <EChart option={storageOption} height={othersText ? 290 : 320} />
              {othersText && (
                <div style={{ textAlign: "center", paddingTop: "8px", fontSize: "12px", color: palette.textSoft, fontWeight: 500 }}>
                  {othersText}
                </div>
              )}
            </div>
          )}
        </ChartCard>
      </div>

      {/* ROW 3 — Admin activity calendar + op-type donut + external share flow */}
      <div className="ysd-grid ysd-grid--2">
        <ChartCard
          title="Admin activity"
          subtitle={`${privilegedTotal.toLocaleString()} privileged ops in last ${range} · darker cells flag busy or unusual days`}
          span={4}
        >
          {loadingOps ? (
            <Loader message="Loading admin activity..." minHeight={320} />
          ) : (
            <EChart option={adminCalendarOption} height={320} />
          )}
        </ChartCard>

        <ChartCard
          title="By op type"
          subtitle={`Share of admin actions in the same ${range} window`}
          span={4}
        >
          {loadingOps ? (
            <Loader message="Loading op types..." minHeight={320} />
          ) : errorOps ? (
            <ErrorHint message={errorOps} onRetry={retry} />
          ) : (adminDonutOption?.series?.[0]?.data?.length || 0) === 0 ? (
            <EmptyHint>No admin operations recorded.</EmptyHint>
          ) : (
            <EChart option={adminDonutOption} height={320} />
          )}
        </ChartCard>

        <ChartCard
          title="Space Collaboration Flow"
          subtitle="Actor → Space → Outcome Lifecycle"
          span={4}
        >
          {loadingExternalShare ? (
            <Loader message="Loading collaboration flow..." minHeight={320} />
          ) : errorExternalShare ? (
            <ErrorHint message={errorExternalShare} onRetry={retry} />
          ) : (sankeyOption?.series?.[0]?.data?.length || 0) === 0 ? (
            <EmptyHint>No collaboration flow detected.</EmptyHint>
          ) : (
            <EChart option={sankeyOption} height={320} />
          )}
        </ChartCard>
      </div>

      {/* ROW 4 — Risk callouts (3 small cards) */}
      <div className="ysd-grid ysd-grid--2">
        <ChartCard
          title="Off-hours activity"
          subtitle={`${offHoursRatio.offHours}% outside 10:00-18:00 local`}
          span={4}
        >
          {loadingOffHours ? (
            <Loader message="Loading off-hours..." minHeight={240} />
          ) : errorOffHours ? (
            <ErrorHint message={errorOffHours} onRetry={retry} />
          ) : (offHoursOption?.series?.[0]?.data?.length || 0) === 0 ? (
            <EmptyHint>No off-hours data available.</EmptyHint>
          ) : (
            <EChart option={offHoursOption} height={240} />
          )}
        </ChartCard>

        <ChartCard
          title={`Denied ops · ${range}`}
          subtitle={`${deniedTotal.toLocaleString()} permission-blocked attempts`}
          span={4}
        >
          {loadingOps ? (
            <Loader message="Loading denied ops..." minHeight={240} />
          ) : errorOps ? (
            <ErrorHint message={errorOps} onRetry={retry} />
          ) : (deniedOption?.series?.[0]?.data?.length || 0) === 0 ? (
            <EmptyHint>No denied operations recorded.</EmptyHint>
          ) : (
            <EChart option={deniedOption} height={240} />
          )}
        </ChartCard>

        <ChartCard
          title="Top operations"
          subtitle="Action mix across the org"
          span={4}
        >
          {loadingOps ? (
            <Loader message="Loading operations..." minHeight={240} />
          ) : errorOps ? (
            <ErrorHint message={errorOps} onRetry={retry} />
          ) : (topOpsOption?.series?.[0]?.data?.length || 0) === 0 ? (
            <EmptyHint>No operations recorded.</EmptyHint>
          ) : (
            <EChart option={topOpsOption} height={240} />
          )}
        </ChartCard>
      </div>

      {/* ROW 5 — Top movers + external shares */}
      <div className="ysd-grid ysd-grid--2">
        <ChartCard
          title={`Top movers · ${range}`}
          subtitle={
            topMoversTab === "uploads" ? "Cumulative upload volume per user · amber bars flag the heaviest" :
              topMoversTab === "shares" ? "Total share + updateShare ops per user · amber bars flag the heaviest" :
                topMoversTab === "activity" ? "Total operations per user · amber bars flag the heaviest" :
                  "Cumulative download volume per user · red bars flag the heaviest"
          }
          span={7}
          right={
            <div className="ysd-toggle">
              <button className={topMoversTab === "downloads" ? "active" : ""} onClick={() => setTopMoversTab("downloads")}>Downloads</button>
              <button className={topMoversTab === "uploads" ? "active" : ""} onClick={() => setTopMoversTab("uploads")}>Uploads</button>
              <button className={topMoversTab === "shares" ? "active" : ""} onClick={() => setTopMoversTab("shares")}>Shares</button>
              <button className={topMoversTab === "activity" ? "active" : ""} onClick={() => setTopMoversTab("activity")}>Activity</button>
            </div>
          }
        >
          {loadingMovers ? (
            <Loader message="Loading movers..." minHeight={320} />
          ) : errorMovers ? (
            <ErrorHint message={errorMovers} onRetry={retry} />
          ) : (topMoversOption?.series?.[0]?.data?.length || 0) === 0 ? (
            <EmptyHint>No data found for this mover metric.</EmptyHint>
          ) : (
            <EChart option={topMoversOption} height={320} />
          )}
        </ChartCard>

        <ChartCard
          title="External shares"
          subtitle="Volume by user · breakdown by adds, updates, and removals"
          span={5}
        >
          {loadingExternalShare ? (
            <Loader message="Loading external shares..." minHeight={320} />
          ) : errorExternalShare ? (
            <ErrorHint message={errorExternalShare} onRetry={retry} />
          ) : (externalShareRankOption?.series?.[0]?.data?.length || 0) === 0 ? (
            <EmptyHint>No external sharing events recorded.</EmptyHint>
          ) : (
            <EChart option={externalShareRankOption} height={320} />
          )}
        </ChartCard>
      </div>

      {/* ROW 6 — Geography (regions + foreign anomalies) */}
      <ChartCard
        title="Access by location"
        subtitle={`${locations.length} regions and countries · `
          + `${locations.filter((r) => r.tier === "trusted").length} trusted, `
          + `${locations.filter((r) => r.tier === "allowed").length} allowed-foreign, `
          + `${locations.filter((r) => r.tier === "blocked").length} blocked`}
        right={
          <div className="ysd-toggle">
            <button className={locationView === "bars" ? "active" : ""} onClick={() => setLocationView("bars")}>Bars</button>
            <button className={locationView === "table" ? "active" : ""} onClick={() => setLocationView("table")}>Table</button>
          </div>
        }
      >
        {loadingLocations ? (
          <Loader message="Loading locations..." minHeight={360} />
        ) : errorLocations ? (
          <ErrorHint message={errorLocations} onRetry={retry} />
        ) : !locations.length ? (
          <EmptyHint>No geographical access data recorded.</EmptyHint>
        ) : locationView === "bars" ? (
          <EChart option={locationOption} height={Math.max(360, locations.length * 30 + 60)} />
        ) : (

          <div className="ysd-table__scroll">
            <table className="ysd-table">
              <thead>
                <tr><th>Location</th><th>Country</th><th>Events</th><th>Users</th><th>Status</th></tr>
              </thead>
              <tbody>
                {[...locations].sort((a, b) => b.events - a.events).map((c, i) => (
                  <tr key={i} className={c.allowed ? "" : "ysd-row--danger"}>
                    <td>{c.label}</td>
                    <td>{c.country}</td>
                    <td>{c.events.toLocaleString()}</td>
                    <td>{c.unique_users}</td>
                    <td>
                      <Badge tone={c.tier === "trusted" ? "success" : c.tier === "allowed" ? "warning" : "danger"} dot>
                        {c.tier}
                      </Badge>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </ChartCard>

      {/* Raw audit log + download-proofs panel will live on its own page —
          intentionally not on the Overview. */}
    </div>
  );
};

export default SummaryDashboard;
